import dayjs from '@/lib/dayjs'

/**
 * Convert a date string (dayjs or Date) to a local date string or dayjs object.
 * @returns - Formatted string or dayjs object.
 */
export function localDateTime(props) {
  let {
    /**
     * The date we want to convert.
     * @type {string | Date | dayjs} - string preferred.
     */
    datetime,
    /**
     * The timezone we want to convert the date to.
     * @type {string}
     */
    timezone,
    /**
     * the format we want to return the date in.
     * @type {string | false} - if false, return the dayjs instance.
     * @default 'll'
     * @ref https://day.js.org/docs/en/display/format
     */
    format = 'll',
    /**
     * If we want to return the date in UTC.
     * @type {boolean}
     * @default false
     * @ref https://day.js.org/docs/en/display/utc
     */
    utc = false,
    /**
     * The unit of time we want to calculate the difference in.
     * @type {string}
     * @default ''
     * @ref https://day.js.org/docs/en/display/difference
     */
    diffUnit = 'day',
    /**
     * If we want to log debug info.
     * @type {boolean}
     * @default false
     */
    debug = false,
    /**
     * A label for the debug info.
     * @type {string}
     */
    debugLabel = '',
  } = props

  if (debug) console.groupCollapsed('%sDEBUG localDateTime =====', debugLabel)

  if (debug) {
    console.log('props: %O', props)
  }

  let dayjsInstance

  // Guess the user's timezone.
  let guessedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  if (!guessedTimezone) {
    guessedTimezone = timezone || 'UTC'
  }

  if (typeof datetime === 'string') {
    // We have a string. This is *probably* what we want.
    // We prefer UTC strings, but we can handle other formats.

    // Check if we have a timezone
    if (!timezone) {
      if (debug) console.log('We DO NOT have a timezone.')

      // We don't have a timezone.
      // Test the format of the string.
      if (datetime.match(/Z$/) !== null) {
        if (debug) console.log('-> UTC string')
        dayjsInstance = dayjs(datetime)
      } else {
        if (debug)
          console.log(
            '-> Unknown string format. Just create a dayjs instance. May have incorrect time.',
          )
        dayjsInstance = dayjs.utc(datetime)
      }
    } else {
      // We have a timezone.
      if (debug) console.log('We have a timezone.')

      if (datetime.match(/Z$/) !== null) {
        if (debug) console.log('-> UTC string. Convert to timezone.')
        dayjsInstance = dayjs.utc(datetime).tz(timezone)
      } else if (
        datetime.match(/^\d{4}-\d{2}-\d{2}[\s|T]\d{2}:\d{2}:\d{2}$/) !== null
      ) {
        if (debug) console.log('-> DB string. Use timezone.')
        dayjsInstance = dayjs.tz(datetime, timezone)
      } else {
        if (debug)
          console.log(
            '-> Just create a dayjs instance using timezone. May have incorrect time.',
          )
        dayjsInstance = dayjs.tz(datetime, timezone)
      }
    }
  } else {
    // Sent in datetime is not a string. This could cause issues if it already has been converted to a dayjs or date object with a timezone.
    if (datetime instanceof Date) {
      if (debug) console.log('Date instance.')
      dayjsInstance = dayjs.utc(datetime)
    } else if (datetime instanceof dayjs) {
      if (debug) console.log('dayjs instance.')
      dayjsInstance = datetime
    }
  }

  if (dayjsInstance['$x']['$timezone'] !== guessedTimezone) {
    if (debug)
      console.log(
        'Timezone not set to guessed timezone of "%s". Changing...',
        guessedTimezone,
      )
    dayjsInstance = dayjsInstance.tz(guessedTimezone)
  } else {
    if (debug)
      console.log('Timezone MATCHES guessed timezone of "%s"', guessedTimezone)
  }

  let returnVal = utc ? dayjsInstance.utc() : dayjsInstance

  switch (format) {
    case false:
      // Do nothing. We return the dayjs instance.
      break
    case 'relative':
      returnVal = dayjs().to(dayjsInstance)
      break
    case 'diff':
      returnVal = dayjsInstance.diff(dayjs(), diffUnit)
      break
    default:
      returnVal = returnVal.format(format)
      break
  }

  if (debug) {
    console.log('returnVal: %O', returnVal)
  }

  if (debug) console.groupEnd('%sDEBUG localDateTime =====', debugLabel)

  return returnVal
}

/**
 * Convert a UTC date string (dayjs or Date) to a date string or dayjs object for the given timezone.
 * @returns - Formatted string or dayjs object.
 */
export function timezoneDateTime(props) {
  let {
    /**
     * The UTC date we want to convert.
     * @type {string | Date | dayjs} - string preferred.
     */
    datetime,
    /**
     * The timezone we want to convert the date to.
     * @type {string} - if empty, return the date in UTC.
     */
    timezone = '',
    /**
     * the format we want to return the date in.
     * @type {string | false} - if false, return the dayjs instance.
     * @default 'll'
     * @ref https://day.js.org/docs/en/display/format
     */
    format = 'll',
    /**
     * The unit of time we want to calculate the difference in.
     * @type {string}
     * @default ''
     * @ref https://day.js.org/docs/en/display/difference
     */
    diffUnit = 'day',
    /**
     * If we want to hide the suffix when using relative time.
     */
    hideRelativeSuffix = false,
  } = props

  let dayjsInstance = dayjs.utc(datetime)

  if (timezone) {
    dayjsInstance = dayjsInstance.tz(timezone)
  }

  switch (format) {
    case false:
      // Do nothing. We return the dayjs instance.
      break
    case 'relative':
      dayjsInstance = dayjs().to(dayjsInstance, hideRelativeSuffix)
      break
    case 'diff':
      dayjsInstance = dayjsInstance.diff(dayjs(), diffUnit)
      break
    default:
      dayjsInstance = dayjsInstance.format(format)
      break
  }

  return dayjsInstance
}

export function formatLiveryUpdatedDate(datetime) {
  if (!datetime) return null

  return localDateTime({
    datetime,
    format: 'MMM D, YYYY',
  })
}

export function test_localDateTime() {
  const tests = [
    {
      datetime: '1983-02-02 10:15:07',
      timezone: 'Europe/London',
    },
    {
      datetime: '1983-02-02T10:15:07Z',
      timezone: 'Europe/London',
    },
    {
      datetime: dayjs.utc('1983-02-02 10:15:07').tz('Europe/London'),
    },
    {
      datetime: dayjs.utc('1983-02-02T10:15:07Z').tz('Europe/London'),
    },
    {
      datetime: '1983-02-02 10:15:07',
      timezone: 'America/Phoenix',
    },
    {
      datetime: '1983-02-02T10:15:07Z',
      timezone: 'America/Phoenix',
    },
    {
      datetime: dayjs.utc('1983-02-02 10:15:07').tz('America/Phoenix'),
    },
    {
      datetime: dayjs.utc('1983-02-02T10:15:07Z').tz('America/Phoenix'),
    },

    {
      datetime: '1983-02-02 10:15:07',
    },
    {
      datetime: '1983-02-02 10:15:07',
    },
    {
      datetime: '1983-02-02T10:15:07Z',
    },
    {
      datetime: dayjs('1983-02-02 10:15:07'),
    },
    {
      datetime: dayjs('1983-02-02T10:15:07Z'),
    },
    {
      datetime: new Date('1983-02-02 10:15:07'),
    },
    {
      datetime: new Date('1983-02-02T10:15:07Z'),
    },

    {
      datetime: '1983-02-02 10:15:07',
      format: 'relative',
    },
    {
      datetime: '2025-02-02 10:15:07',
      format: 'relative',
    },
    {
      datetime: '2024-03-29 10:15:07',
      format: 'relative',
    },
    {
      datetime: dayjs().add(5, 'minutes'),
      format: 'relative',
    },
    {
      datetime: dayjs().subtract(5, 'minutes'),
      format: 'relative',
    },
  ]
  tests.forEach((test, index) => {
    localDateTime({
      debugLabel: `TEST ${index + 1}: `,
      format: 'llll',
      ...test,
      debug: true,
    })
  })
}
